import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Grid, Button, FormControl, InputLabel, Select, MenuItem} from '@material-ui/core';
import {TextBox} from '../../Core/UI';
import AddressVerificationResult from './AddressVerificationResult';
import RootStore from '../../Core/mobx/RootStore';
import {AddressKey} from '../../Core/mobx/AddressVerificationStore/AddressVerificationStore';
import {STATE_OPTIONS} from '../constants';
import {Autocomplete, useJsApiLoader} from '@react-google-maps/api';

const LIBRARIES: ['places'] = ['places'];

type AddressFields = {
    street: string;
    city: string;
    state: string;
    zip: string;
    unit: string;
};

type AddressVerificationSectionProps = {
    addressKey: AddressKey;
    fields: AddressFields;
    stateOptions?: string[];
    onFieldChange: (field: keyof AddressFields, value: string) => void;
    onAcceptStandardized: () => void;
    label?: string;
};

const required = (value?: string) => (
    <span>
        {value && value} <span style={{color: 'red'}}>*</span>
    </span>
);

const getVerifiedParams = (addressKey: AddressKey) => {
    const approval = RootStore.agentPortalStore.parameterStore.approval;
    return addressKey === 'client'
        ? {verified: approval.clientAddressVerified, snapshot: approval.clientAddressVerifiedSnapshot}
        : {verified: approval.policyOwnerAddressVerified, snapshot: approval.policyOwnerAddressVerifiedSnapshot};
};

const AddressVerificationSection = observer(
    ({
        addressKey,
        fields,
        stateOptions,
        onFieldChange,
        onAcceptStandardized,
        label,
    }: AddressVerificationSectionProps) => {
        const store = RootStore.addressVerificationStore;
        const verificationState = store.getState(addressKey);
        const canSubmit = store.canSubmit(addressKey);
        const options = stateOptions ?? STATE_OPTIONS;
        const prevCanSubmit = useRef(canSubmit);

        const addressFilled = !!(fields.street && fields.city && fields.state && fields.zip);

        const {isLoaded, loadError} = useJsApiLoader({
            googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY || '',
            libraries: LIBRARIES,
            language: 'en-US',
            region: 'us',
        });

        const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

        const onPlacesChanged = () => {
            const place = autocomplete?.getPlace();
            if (!place?.address_components) return;

            let streetNumber = '';
            let streetName = '';
            let city = '';
            let state = '';
            let zip = '';

            place.address_components.forEach((c: google.maps.GeocoderAddressComponent) => {
                switch (c.types[0]) {
                    case 'street_number':
                        streetNumber = c.short_name;
                        break;
                    case 'route':
                        streetName = c.long_name;
                        break;
                    case 'neighborhood':
                    case 'locality':
                        city = c.long_name;
                        break;
                    case 'administrative_area_level_1':
                        state = c.short_name;
                        break;
                    case 'postal_code':
                        zip = c.short_name;
                        break;
                }
            });

            const street = `${streetNumber} ${streetName}`.trim();
            handleChange('street', street);
            handleChange('city', city);
            handleChange('state', state);
            handleChange('zip', zip);
        };

        // Save verified state when canSubmit becomes true
        useEffect(() => {
            if (canSubmit && !prevCanSubmit.current) {
                const {verified, snapshot} = getVerifiedParams(addressKey);
                verified.value = 'true';
                const snapshotData = {
                    street: fields.street,
                    city: fields.city,
                    state: fields.state,
                    zip: fields.zip,
                    unit: fields.unit || undefined,
                };
                snapshot.value = JSON.stringify(snapshotData);
            }
            prevCanSubmit.current = canSubmit;
        }, [canSubmit, addressKey, fields]);

        const clearVerifiedState = () => {
            const {verified, snapshot} = getVerifiedParams(addressKey);
            verified.value = '';
            snapshot.value = '';
        };

        const handleChange = (field: keyof AddressFields, value: string) => {
            onFieldChange(field, value);
            store.markStale(addressKey);
            clearVerifiedState();
        };

        const verify = () => {
            store.verifyAddress(addressKey, {
                street: fields.street,
                city: fields.city,
                state: fields.state,
                zip: fields.zip,
                unit: fields.unit || undefined,
            });
        };

        const handleAcceptStandardized = () => {
            clearVerifiedState();
            const state = store.getState(addressKey);
            const std = state.result?.standardizedAddress;
            onAcceptStandardized();
            // Auto re-verify with the standardized address
            if (std) {
                store.verifyAddress(addressKey, {
                    street: std.street,
                    city: std.city,
                    state: std.state,
                    zip: std.zip,
                    unit: std.unit || undefined,
                });
            }
        };

        const buttonLabel = canSubmit
            ? 'Verified'
            : verificationState.result && !verificationState.isStale
              ? 'Re-verify'
              : 'Verify Address';

        return (
            <div style={{paddingTop: 8}}>
                {label && (
                    <div style={{marginBottom: 4}}>
                        <div style={{fontSize: 14, fontWeight: 600, color: '#5D6CC1'}}>{label}</div>
                        <div style={{fontSize: 12, color: '#888'}}>Address Verification</div>
                    </div>
                )}
                <Grid container spacing={1}>
                    <Grid item xs={5}>
                        {isLoaded && !loadError ? (
                            <Autocomplete
                                onLoad={setAutocomplete}
                                onUnmount={() => setAutocomplete(null)}
                                options={{componentRestrictions: {country: 'us'}}}
                                onPlaceChanged={onPlacesChanged}
                                types={['address']}
                            >
                                <TextBox
                                    id={`${addressKey}-street`}
                                    label={required('Street Address')}
                                    value={fields.street}
                                    onChange={(value) => handleChange('street', value)}
                                />
                            </Autocomplete>
                        ) : (
                            <TextBox
                                id={`${addressKey}-street`}
                                label={required('Street Address')}
                                value={fields.street}
                                onChange={(value) => handleChange('street', value)}
                            />
                        )}
                    </Grid>
                    <Grid item xs={3}>
                        <TextBox
                            id={`${addressKey}-city`}
                            label={required('City')}
                            value={fields.city}
                            onChange={(value) => handleChange('city', value)}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControl fullWidth variant="outlined" style={{marginTop: 8}}>
                            <InputLabel id={`${addressKey}-state-label`}>State {required('')}</InputLabel>
                            <Select
                                labelId={`${addressKey}-state-label`}
                                id={`${addressKey}-state`}
                                value={fields.state}
                                label="State *"
                                onChange={(e) => handleChange('state', e.target.value as string)}
                            >
                                {options.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                        <TextBox
                            id={`${addressKey}-zip`}
                            label={required('Zip')}
                            value={fields.zip}
                            onChange={(value) => handleChange('zip', value)}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                        <TextBox
                            id={`${addressKey}-unit`}
                            label="Unit (optional)"
                            placeholder="Apt, Suite, etc."
                            value={fields.unit}
                            onChange={(value) => handleChange('unit', value)}
                        />
                    </Grid>
                    <Grid item xs={3} style={{display: 'flex', alignItems: 'center', paddingTop: 16}}>
                        <Button
                            variant="contained"
                            color={canSubmit ? 'default' : 'primary'}
                            size="small"
                            disabled={!addressFilled || verificationState.isVerifying || canSubmit}
                            onClick={verify}
                            style={canSubmit ? {backgroundColor: '#4caf50', color: '#fff'} : undefined}
                        >
                            {verificationState.isVerifying ? 'Verifying...' : buttonLabel}
                        </Button>
                    </Grid>
                </Grid>
                <AddressVerificationResult
                    state={verificationState}
                    canSubmit={canSubmit}
                    onAcceptStandardized={handleAcceptStandardized}
                />
            </div>
        );
    },
);

export default AddressVerificationSection;
